import {
  TableCell,
  TableRow,
  TextField,
  Autocomplete,
  IconButton,
  createFilterOptions,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ReactInputMask from "react-input-mask";
import { useEffect, useState } from "react";
import moment from "moment";
import { verificarCuitListaNegra } from "../../../../../../../proveedores/cheques_lista_negra/ApiServices";
import { formatCuit } from "../../../../../../../proveedores/pago_proveedores/utils";
import { normalizarCuit } from "../../../../../../../proveedores/cheques_lista_negra/listaNegra.constants";

const TIPO_QR = 5; // cac_tipos_pago.id del tipo "QR"

export default function InstrumentoRow({
  instrumento,
  index,
  cuit,
  tiposInstrumento,
  bancos,
  mostrarFechaDePago = false,
  onUpdate,
  onRemove,
  handleImporteChange,
  onSelectTipoQr,
}) {
  const soloLecturaQr = Boolean(instrumento.soloLecturaQr);
  const requiereCuitFirma =
    instrumento.tipo_id === 4 ||
    instrumento.tipo_id === 3 ||
    instrumento.tipo_id === 6;

  const esTransferencia = instrumento.tipo_id === 4;

  // Cheque (3) o E-cheq (6): la fecha de pago (cobro) es obligatoria y puede ser futura.
  const requiereFechaDePago =
    instrumento.tipo_id === 3 || instrumento.tipo_id === 6;

  const codigoBanco = esTransferencia ? "codigo_transferencias" : "codigo_bcra";

  const obtenerCodigoBanco = (banco) => banco?.[codigoBanco] ?? "No hay codigo";

  console.log("Bancos: ", bancos);
  console.log("Tipos instrumentos: ", tiposInstrumento);

  const filterOptions = createFilterOptions({
    stringify: (option) => `${option.nombre} ${obtenerCodigoBanco(option)}`,
  });

  const [localImporteBancario, setLocalImporteBancario] = useState(
    instrumento.importe || "",
  );
  const [listaNegraMsg, setListaNegraMsg] = useState("");
  const [verificandoCuit, setVerificandoCuit] = useState(false);

  useEffect(() => {
    setLocalImporteBancario(instrumento.importe || "");
  }, [instrumento]);

  const verificarCuitFirma = async (value) => {
    const cuitNorm = normalizarCuit(value);
    if (!requiereCuitFirma || cuitNorm.length < 11) {
      setListaNegraMsg("");
      return;
    }
    setVerificandoCuit(true);
    try {
      const data = await verificarCuitListaNegra(cuitNorm);
      if (data?.bloqueado) {
        setListaNegraMsg(
          data?.msg ||
            data?.message ||
            `CUIT ${formatCuit(cuitNorm)} bloqueado en lista negra de emisores`,
        );
      } else {
        setListaNegraMsg("");
      }
    } catch {
      setListaNegraMsg("");
    } finally {
      setVerificandoCuit(false);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Autocomplete
          options={tiposInstrumento}
          noOptionsText="No hay opciones"
          getOptionLabel={(option) => option.valor}
          value={
            tiposInstrumento.find((t) => t.id === instrumento.tipo_id) || null
          }
          disabled={soloLecturaQr}
          onChange={(e, newValue) => {
            if (newValue?.id === TIPO_QR) {
              // La fila la resuelve el diálogo de créditos QR, no el formulario manual.
              onSelectTipoQr?.(index);
              return;
            }
            onUpdate(index, "tipo_id", newValue?.id || null);
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Tipo" size="small" />
          )}
          size="small"
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          options={bancos}
          filterOptions={filterOptions}
          noOptionsText="No hay opciones"
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;

            return `${option.nombre} (${obtenerCodigoBanco(option)})`;
          }}
          value={bancos.find((b) => b.id === instrumento.banco_id) || null}
          disabled={soloLecturaQr}
          onChange={(e, newValue) =>
            onUpdate(index, "banco_id", newValue?.id || null)
          }
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Banco"
              size="small"
              disabled={soloLecturaQr}
              onBlur={(e) => {
                const value = e.target.value?.trim();

                const match = bancos.find(
                  (b) => String(b[codigoBanco]) === value,
                );
                if (match) {
                  onUpdate(index, "banco_id", match.id);
                }
              }}
            />
          )}
          size="small"
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          value={instrumento.nro_comprobante}
          disabled={soloLecturaQr}
          onChange={(e) => {
            const value = e.target.value.replace("-", "");
            onUpdate(index, "nro_comprobante", value);
          }}
          placeholder="000000000000"
          size="small"
          sx={{ minWidth: 120 }}
        ></TextField>
      </TableCell>
      <TableCell>
        <DatePicker
          value={
            instrumento.dia && instrumento.mes && instrumento.ano
              ? moment(
                  `20${String(instrumento.ano).padStart(2, "0")}-${String(
                    instrumento.mes,
                  ).padStart(2, "0")}-${String(instrumento.dia).padStart(
                    2,
                    "0",
                  )}`,
                  "YYYY-MM-DD",
                )
              : null
          }
          onChange={(value) => {
            if (value && value.isValid()) {
              onUpdate(index, "dia", value.format("DD"));
              onUpdate(index, "mes", value.format("MM"));
              onUpdate(index, "ano", value.format("YY"));
            } else {
              onUpdate(index, "dia", "");
              onUpdate(index, "mes", "");
              onUpdate(index, "ano", "");
            }
          }}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              label: "Fecha de emisión",
              placeholder: "DD/MM/AAAA",
              sx: { minWidth: 170 },
            },
          }}
        />
      </TableCell>
      {mostrarFechaDePago && (
        <TableCell>
          {requiereFechaDePago ? (
            <DatePicker
              value={
                instrumento.fecha_de_pago
                  ? moment(instrumento.fecha_de_pago, "YYYY-MM-DD")
                  : null
              }
              onChange={(value) => {
                onUpdate(
                  index,
                  "fecha_de_pago",
                  value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                );
              }}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  label: "Fecha de pago",
                  placeholder: "DD/MM/AAAA",
                  error:
                    parseFloat(instrumento.importe) > 0 &&
                    !instrumento.fecha_de_pago,
                  sx: { minWidth: 170 },
                },
              }}
            />
          ) : null}
        </TableCell>
      )}
      <TableCell>
        <TextField
          type="number"
          onWheel={(e) => e.target.blur()}
          sx={{
            "& input[type=number]": {
              MozAppearance: "textfield", // Firefox
            },
            "& input[type=number]::-webkit-outer-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            minWidth: 100,
          }}
          value={localImporteBancario}
          disabled={soloLecturaQr}
          onBlur={(e) =>
            handleImporteChange(e, (value) => onUpdate(index, "importe", value))
          }
          onChange={(e) => setLocalImporteBancario(e.target.value)}
          placeholder="0.00"
          size="small"
        />
      </TableCell>
      <TableCell>
        <ReactInputMask
          mask="99-99999999-9"
          value={instrumento.cuit_firma}
          disabled={!requiereCuitFirma}
          maskChar=""
          onChange={(e) => {
            const value = e.target.value;

            if (value.startsWith("99")) {
              onUpdate(index, "cuit_firma", cuit);
            } else {
              onUpdate(index, "cuit_firma", value);
            }
            if (listaNegraMsg) setListaNegraMsg("");
          }}
          onBlur={(e) => verificarCuitFirma(e.target.value)}
        >
          {() => (
            <TextField
              size="small"
              disabled={!requiereCuitFirma}
              placeholder={requiereCuitFirma ? "20-12345678-9" : "No requerido"}
              error={
                (requiereCuitFirma &&
                  parseFloat(instrumento.importe) > 0 &&
                  !instrumento.cuit_firma) ||
                Boolean(listaNegraMsg)
              }
              helperText={
                listaNegraMsg ||
                (verificandoCuit ? "Verificando CUIT…" : undefined)
              }
              sx={{ minWidth: 130 }}
            />
          )}
        </ReactInputMask>
      </TableCell>
      <TableCell>
        <IconButton size="small" color="error" onClick={() => onRemove(index)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
